import { z } from 'zod';
import { CharacterPortraitSchema } from './party.js';
/**
 * Friends (ADR 0033). A friendship is asked of a player in the game and
 * taken once they accept; it then lasts, both ways, until one of them ends
 * it. The server keeps the list and says who is online; a client only names
 * whom to ask, answers, or removes.
 */
/** A character's friends at most: a list one still reads at a glance. */
export const FRIENDS_MAX = 50;
/** A request not answered in this time lapses. */
export const FRIEND_REQUEST_TTL_MS = 60_000;
const CharacterIdSchema = z.string().min(1).max(64);
export const FriendRequestMessageSchema = z.object({ name: z.string().trim().min(1).max(32) });
/** Answer the request of `from` (a character id, as `friend_requested` gave it). */
export const FriendAnswerMessageSchema = z.object({
    from: CharacterIdSchema,
    accept: z.boolean(),
});
export const FriendRemoveMessageSchema = z.object({ characterId: CharacterIdSchema });
export const FriendSchema = z.object({
    id: CharacterIdSchema,
    name: z.string().min(1),
    online: z.boolean(),
    /** In the game but away from the keyboard (ADR 0080). */
    away: z.boolean().default(false),
    /** Where they play, while online. */
    zoneId: z.string().min(1).nullable(),
    /** Their head as the zone shows them, while online; null away. */
    portrait: CharacterPortraitSchema.nullable(),
});
/** To the owner, on joining and after every change: online friends first, then by name. */
export const FriendsStateSchema = z.object({
    friends: z.array(FriendSchema).max(FRIENDS_MAX),
});
/**
 * To the owner, whenever it changes: the tiles of the friends playing in the
 * owner's own zone, for the map and the minimap. Friends elsewhere are not
 * in it: the maps show one zone.
 */
export const FriendPositionsSchema = z.object({
    positions: z.array(z.object({
        id: CharacterIdSchema,
        name: z.string().min(1),
        x: z.number().int().min(0),
        y: z.number().int().min(0),
    })),
});
/** To the asked player: who asks, and how long the question stands. */
export const FriendRequestedSchema = z.object({
    from: CharacterIdSchema,
    name: z.string().min(1),
    expiresInMs: z.number().int().min(0),
});
export const FRIEND_EVENT_KINDS = [
    /** To the one who asked: the request is on its way. */
    'requested',
    'declined',
    'expired',
    'added',
    'removed',
    /** A friend came into the game. */
    'online',
];
export const FriendEventSchema = z.object({
    kind: z.enum(FRIEND_EVENT_KINDS),
    /** The other player. */
    name: z.string().min(1),
});
export const FRIEND_REFUSALS = [
    'invalid',
    /** Nobody of that name is in the game. */
    'not-found',
    'self',
    'already-friends',
    /** A request to that player is already waiting. */
    'pending',
    /** One of the two lists is full. */
    'full',
    /** The request answered no longer stands. */
    'no-request',
    'not-friends',
];
export const FriendRejectedSchema = z.object({ reason: z.enum(FRIEND_REFUSALS) });
//# sourceMappingURL=friends.js.map